import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Card, CardContent, Grid,
  TextField, LinearProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import { SwapHoriz, Add, Check, Close, ThumbDown } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  pending:   { color: 'warning', label: '⏳ Awaiting Response' },
  accepted:  { color: 'info',    label: '✅ Accepted – Pending Approval' },
  rejected:  { color: 'error',   label: '❌ Rejected by Colleague' },
  approved:  { color: 'success', label: '✅ Approved' },
  declined:  { color: 'error',   label: '🚫 Declined by Manager' },
  cancelled: { color: 'default', label: '↩️ Cancelled' },
};

interface Swap {
  id: string; requesterId: string; targetId: string;
  requesterDate: string; requesterShiftName: string; requesterShiftTime: string;
  targetDate: string; targetShiftName: string; targetShiftTime: string;
  status: string; reason?: string; targetResponse?: string; approverNote?: string;
  requester?: { firstName: string; lastName: string; department?: string };
  target?: { firstName: string; lastName: string; department?: string };
  approvedBy?: { firstName: string; lastName: string };
  createdAt?: string;
}

export default function ShiftSwap() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager', 'manager'].includes(user?.role ?? '');
  const myEmpId = user?.employee?.id;

  const [swaps, setSwaps] = useState<Swap[]>([]);
  const [incoming, setIncoming] = useState<Swap[]>([]);
  const [pendingApproval, setPendingApproval] = useState<Swap[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [tab, setTab] = useState<'mine' | 'incoming' | 'approval'>('mine');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<any>({ reason: '' });

  // Respond dialog (target accepts/rejects)
  const [respondDlg, setRespondDlg] = useState<{ id: string; accept: boolean; response: string } | null>(null);
  // Approve/decline dialog (manager)
  const [approvalDlg, setApprovalDlg] = useState<{ id: string; action: 'approve' | 'decline'; note: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [mine, inc, pend, st] = await Promise.all([
        apiFetch('/api/shift-swaps'),
        apiFetch('/api/shift-swaps/incoming'),
        isHR ? apiFetch('/api/shift-swaps/pending-approval') : Promise.resolve([]),
        apiFetch('/api/shift-swaps/stats'),
      ]);
      setSwaps(mine || []); setIncoming(inc || []); setPendingApproval(pend || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [isHR]);

  useEffect(() => { load(); }, [load]);

  const submitRequest = async () => {
    try {
      await apiFetch('/api/shift-swaps', { method: 'POST', body: JSON.stringify(form) });
      setFormOpen(false); setForm({ reason: '' }); load();
    } catch (e: any) { setError(e.message); }
  };

  const respond = async () => {
    if (!respondDlg) return;
    try {
      await apiFetch(`/api/shift-swaps/${respondDlg.id}/respond`, {
        method: 'PATCH', body: JSON.stringify({ accept: respondDlg.accept, response: respondDlg.response }),
      });
      setRespondDlg(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const doApproval = async () => {
    if (!approvalDlg) return;
    try {
      await apiFetch(`/api/shift-swaps/${approvalDlg.id}/${approvalDlg.action}`, {
        method: 'PATCH', body: JSON.stringify({ approverNote: approvalDlg.note }),
      });
      setApprovalDlg(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const cancel = async (id: string) => {
    try { await apiFetch(`/api/shift-swaps/${id}/cancel`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const fmtDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-HK', { weekday: 'short', day: 'numeric', month: 'short' });

  const activeList = tab === 'mine' ? swaps : tab === 'incoming' ? incoming : pendingApproval;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHoriz sx={{ fontSize: 32, color: '#1565c0' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Shift Swap Requests</Typography>
            <Typography variant="body2" color="text.secondary">Request to trade shifts with a colleague</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>Request Swap</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total',            value: stats.total,           color: '#37474f' },
            { label: 'Pending Response', value: stats.pending,         color: '#e65100' },
            { label: 'Awaiting Approval',value: stats.pendingApproval, color: '#1565c0' },
            { label: 'Approved',         value: stats.approved,        color: '#2e7d32' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tab toggle */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`My Requests (${swaps.length})`} onClick={() => setTab('mine')}
          color={tab === 'mine' ? 'primary' : 'default'} variant={tab === 'mine' ? 'filled' : 'outlined'} />
        <Chip label={`Incoming Swaps (${incoming.length})`} onClick={() => setTab('incoming')}
          color={tab === 'incoming' ? 'warning' : 'default'} variant={tab === 'incoming' ? 'filled' : 'outlined'} />
        {isHR && <Chip label={`Pending Approval (${pendingApproval.length})`} onClick={() => setTab('approval')}
          color={tab === 'approval' ? 'success' : 'default'} variant={tab === 'approval' ? 'filled' : 'outlined'} />}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><b>Requester</b></TableCell>
              <TableCell><b>Their Shift</b></TableCell>
              <TableCell><b>↔</b></TableCell>
              <TableCell><b>Colleague</b></TableCell>
              <TableCell><b>Colleague's Shift</b></TableCell>
              <TableCell><b>Reason</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeList.map(s => (
              <TableRow key={s.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{s.requester?.firstName} {s.requester?.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.requester?.department}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{s.requesterShiftName}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtDate(s.requesterDate)} · {s.requesterShiftTime}</Typography>
                </TableCell>
                <TableCell><SwapHoriz color="action" /></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{s.target?.firstName} {s.target?.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.target?.department}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{s.targetShiftName}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtDate(s.targetDate)} · {s.targetShiftTime}</Typography>
                </TableCell>
                <TableCell><Typography variant="caption" sx={{ maxWidth: 120, display: 'block' }} noWrap>{s.reason || '—'}</Typography></TableCell>
                <TableCell><Chip label={STATUS_CFG[s.status]?.label} size="small" color={STATUS_CFG[s.status]?.color} /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {/* Target responds */}
                    {tab === 'incoming' && s.status === 'pending' && s.targetId === myEmpId && (
                      <>
                        <Tooltip title="Accept Swap">
                          <IconButton size="small" color="success" onClick={() => setRespondDlg({ id: s.id, accept: true, response: '' })}><Check fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Reject Swap">
                          <IconButton size="small" color="error" onClick={() => setRespondDlg({ id: s.id, accept: false, response: '' })}><ThumbDown fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    )}
                    {/* Manager approves */}
                    {tab === 'approval' && s.status === 'accepted' && isHR && (
                      <>
                        <Tooltip title="Approve & Apply">
                          <IconButton size="small" color="success" onClick={() => setApprovalDlg({ id: s.id, action: 'approve', note: '' })}><Check fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Decline">
                          <IconButton size="small" color="error" onClick={() => setApprovalDlg({ id: s.id, action: 'decline', note: '' })}><Close fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    )}
                    {/* Requester cancels */}
                    {s.requesterId === myEmpId && s.status === 'pending' && (
                      <Tooltip title="Cancel"><IconButton size="small" onClick={() => cancel(s.id)}><Close fontSize="small" /></IconButton></Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {activeList.length === 0 && !loading && (
              <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No shift swap requests.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── REQUEST FORM ── */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Request Shift Swap
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setFormOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>Enter both shift assignment IDs. You can find these in your schedule.</Alert>
            <TextField label="Your Shift Assignment ID" fullWidth required value={form.requesterAssignmentId ?? ''}
              onChange={e => setForm((p: any) => ({ ...p, requesterAssignmentId: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Your Shift Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.requesterDate ?? ''} onChange={e => setForm((p: any) => ({ ...p, requesterDate: e.target.value }))} />
              <TextField label="Your Shift Name" fullWidth value={form.requesterShiftName ?? ''}
                onChange={e => setForm((p: any) => ({ ...p, requesterShiftName: e.target.value }))} />
            </Box>
            <TextField label="Your Shift Time (e.g. 09:00–17:00)" fullWidth value={form.requesterShiftTime ?? ''}
              onChange={e => setForm((p: any) => ({ ...p, requesterShiftTime: e.target.value }))} />

            <Box sx={{ borderTop: '1px solid #eee', pt: 2 }}>
              <TextField label="Colleague Employee ID" fullWidth required value={form.targetId ?? ''}
                onChange={e => setForm((p: any) => ({ ...p, targetId: e.target.value }))} />
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField label="Colleague's Assignment ID" fullWidth required value={form.targetAssignmentId ?? ''}
                  onChange={e => setForm((p: any) => ({ ...p, targetAssignmentId: e.target.value }))} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField label="Colleague's Shift Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                  value={form.targetDate ?? ''} onChange={e => setForm((p: any) => ({ ...p, targetDate: e.target.value }))} />
                <TextField label="Colleague's Shift Name" fullWidth value={form.targetShiftName ?? ''}
                  onChange={e => setForm((p: any) => ({ ...p, targetShiftName: e.target.value }))} />
              </Box>
              <TextField label="Colleague's Shift Time" fullWidth sx={{ mt: 2 }} value={form.targetShiftTime ?? ''}
                onChange={e => setForm((p: any) => ({ ...p, targetShiftTime: e.target.value }))} />
            </Box>

            <TextField label="Reason (optional)" fullWidth multiline rows={2} value={form.reason}
              onChange={e => setForm((p: any) => ({ ...p, reason: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitRequest}
            disabled={!form.requesterAssignmentId || !form.targetId || !form.targetAssignmentId || !form.requesterDate || !form.targetDate}>
            Send Swap Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── RESPOND DIALOG ── */}
      <Dialog open={!!respondDlg} onClose={() => setRespondDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{respondDlg?.accept ? '✅ Accept Swap' : '❌ Reject Swap'}</DialogTitle>
        <DialogContent dividers>
          <TextField label="Message (optional)" fullWidth value={respondDlg?.response ?? ''}
            onChange={e => setRespondDlg(p => p ? { ...p, response: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRespondDlg(null)}>Cancel</Button>
          <Button variant="contained" color={respondDlg?.accept ? 'success' : 'error'} onClick={respond}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* ── APPROVAL DIALOG ── */}
      <Dialog open={!!approvalDlg} onClose={() => setApprovalDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{approvalDlg?.action === 'approve' ? '✅ Approve Swap' : '🚫 Decline Swap'}</DialogTitle>
        <DialogContent dividers>
          <TextField label={approvalDlg?.action === 'decline' ? 'Reason *' : 'Note (optional)'}
            fullWidth value={approvalDlg?.note ?? ''}
            onChange={e => setApprovalDlg(p => p ? { ...p, note: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDlg(null)}>Cancel</Button>
          <Button variant="contained" color={approvalDlg?.action === 'approve' ? 'success' : 'error'}
            disabled={approvalDlg?.action === 'decline' && !approvalDlg?.note?.trim()}
            onClick={doApproval}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
